import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { ProcurementStatus } from 'scheduler-types';
import { apiFetch } from '../lib/api';

// --- Types ---

export interface ProcurementClaimedBy {
  id: string;
  name: string | null;
}

export interface ProcurementListItem {
  id: string;
  partNumber: string;
  quantity: number;
  status: ProcurementStatus;
  claimedBy: ProcurementClaimedBy | null;
  claimedAt: string | null;
  gpPoNumber: string | null;
  expectedDockDate: string | null;
  vendorId: string | null;
  vendorName: string | null;
  needByDate: string | null;
  orderByDate: string | null;
  orderedAt: string | null;
  confirmedAt: string | null;
  trackingNumber: string | null;
  trackingAt: string | null;
  receivedAt: string | null;
  lastAttemptAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ShortageItem {
  partNumber: string;
  buildLocation: string;
  quantityNeeded: number;
  quantityOnHand: number;
  quantityOnHandElsewhere: number;
  shadowInventory: number;
  pendingRelief: number;
  effectiveAvailable: number;
  delta: number;
  isShort: boolean;
  transferRequired: boolean;
}

export interface ProcurementFilters {
  status?: ProcurementStatus;
  partNumber?: string;
  limit?: number;
  offset?: number;
}

export interface ProcurementListResponse {
  data: ProcurementListItem[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

// --- Fetch Functions ---

async function fetchProcurementItems(
  filters: ProcurementFilters = {}
): Promise<ProcurementListResponse> {
  const params = new URLSearchParams();

  if (filters.status) {
    params.append('status', filters.status);
  }
  if (filters.partNumber) {
    params.append('partNumber', filters.partNumber);
  }
  if (filters.limit !== undefined) {
    params.append('limit', String(filters.limit));
  }
  if (filters.offset !== undefined) {
    params.append('offset', String(filters.offset));
  }

  const queryString = params.toString();
  const path = `/api/v1/procurement${queryString ? `?${queryString}` : ''}`;

  return apiFetch<ProcurementListResponse>(path);
}

async function claimItem(id: string): Promise<{ data: ProcurementListItem }> {
  return apiFetch<{ data: ProcurementListItem }>(`/api/v1/procurement/${id}/claim`, {
    method: 'POST',
  });
}

async function releaseItem(id: string): Promise<{ data: ProcurementListItem }> {
  return apiFetch<{ data: ProcurementListItem }>(`/api/v1/procurement/${id}/release`, {
    method: 'POST',
  });
}

async function markOrdered(
  id: string,
  gpPoNumber: string
): Promise<{ data: ProcurementListItem }> {
  return apiFetch<{ data: ProcurementListItem }>(`/api/v1/procurement/${id}/ordered`, {
    method: 'POST',
    body: JSON.stringify({ gpPoNumber }),
  });
}

// --- Hooks ---

export function useProcurementItems(filters: ProcurementFilters = {}) {
  return useQuery({
    queryKey: ['procurement', 'items', filters],
    queryFn: () => fetchProcurementItems(filters),
    staleTime: 30000,
  });
}

export function useClaimProcurement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => claimItem(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['procurement'] });
    },
  });
}

export function useReleaseProcurement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => releaseItem(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['procurement'] });
    },
  });
}

export function useMarkOrdered() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, gpPoNumber }: { id: string; gpPoNumber: string }) =>
      markOrdered(id, gpPoNumber),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['procurement'] });
    },
  });
}
